/**
 * Motor de cálculo de risco do PGR Bradesco: resolução texto/ID -> ID de referência e
 * consulta à Matriz de Risco 5x5 (Probabilidade x Severidade).
 *
 * Extraído para cá conforme pedido explícito da spec ("extrair a lógica de resolução
 * texto->ID e consulta à matriz para um helper compartilhado em bradesco/pgr/_helpers/").
 *
 * Todo o processamento é POSICIONAL: dado um vetor de respostas de Probabilidade e um
 * vetor de respostas de Severidade (mesmo tamanho), a posição i de um corresponde à
 * posição i do outro e gera a posição i do vetor de Níveis de Risco. Nunca reduzir os
 * vetores a um valor único.
 */

export const PROBABILIDADE_REFERENCIA = [
  { ID: 1, descricao: 'Muito Baixo' },
  { ID: 2, descricao: 'Baixo' },
  { ID: 3, descricao: 'Moderado' },
  { ID: 4, descricao: 'Alto' },
  { ID: 5, descricao: 'Severo ou Crítico' }
]

export const SEVERIDADE_REFERENCIA = [
  { ID: 1, descricao: 'Irrelevante' },
  { ID: 2, descricao: 'Baixo' },
  { ID: 3, descricao: 'Moderado' },
  { ID: 4, descricao: 'Alto' },
  { ID: 5, descricao: 'Extremo' }
]

// "Matriz de Risco - Classificação de Nível de Risco Ocupacional" (a matriz única do
// documento, usada tanto no texto de referência quanto no cálculo dinâmico do
// Inventário de Riscos — NÃO é a matriz "Final"/ERGOCHECK).
export const MATRIZ_RISCO = [
  {
    probabilidadeID: 1,
    severidades: [
      { severidadeID: 1, resultado: 1, classificacao: 'Trivial' },
      { severidadeID: 2, resultado: 2, classificacao: 'Baixo' },
      { severidadeID: 3, resultado: 3, classificacao: 'Baixo' },
      { severidadeID: 4, resultado: 4, classificacao: 'Moderado' },
      { severidadeID: 5, resultado: 5, classificacao: 'Moderado' }
    ]
  },
  {
    probabilidadeID: 2,
    severidades: [
      { severidadeID: 1, resultado: 2, classificacao: 'Baixo' },
      { severidadeID: 2, resultado: 4, classificacao: 'Moderado' },
      { severidadeID: 3, resultado: 6, classificacao: 'Moderado' },
      { severidadeID: 4, resultado: 8, classificacao: 'Alto' },
      { severidadeID: 5, resultado: 10, classificacao: 'Alto' }
    ]
  },
  {
    probabilidadeID: 3,
    severidades: [
      { severidadeID: 1, resultado: 3, classificacao: 'Baixo' },
      { severidadeID: 2, resultado: 6, classificacao: 'Moderado' },
      { severidadeID: 3, resultado: 9, classificacao: 'Alto' },
      { severidadeID: 4, resultado: 12, classificacao: 'Alto' },
      { severidadeID: 5, resultado: 15, classificacao: 'Crítico' }
    ]
  },
  {
    probabilidadeID: 4,
    severidades: [
      { severidadeID: 1, resultado: 4, classificacao: 'Moderado' },
      { severidadeID: 2, resultado: 8, classificacao: 'Alto' },
      { severidadeID: 3, resultado: 12, classificacao: 'Alto' },
      { severidadeID: 4, resultado: 16, classificacao: 'Crítico' },
      { severidadeID: 5, resultado: 20, classificacao: 'Crítico' }
    ]
  },
  {
    probabilidadeID: 5,
    severidades: [
      { severidadeID: 1, resultado: 5, classificacao: 'Moderado' },
      { severidadeID: 2, resultado: 10, classificacao: 'Alto' },
      { severidadeID: 3, resultado: 15, classificacao: 'Crítico' },
      { severidadeID: 4, resultado: 20, classificacao: 'Crítico' },
      { severidadeID: 5, resultado: 25, classificacao: 'Crítico' }
    ]
  }
]

const normalizeText = value =>
  typeof value === 'string' ? value.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') : value

/**
 * Resolve um valor bruto (número 1-5, texto de classificação, ou objeto de resposta
 * {id,name}/{name}) para o ID (1-5) do vetor de referência informado.
 * Aceita número, texto, ID já numérico, ou combinação mista — nunca lança para entrada
 * inesperada, retorna null quando não for possível resolver.
 *
 * Reforço (2026-09-02, verificação da nova regra de Probabilidade/Severidade/Nível de
 * Risco pedida pela Julia Buzzo): as perguntas de Probabilidade/Severidade são de
 * resposta única, mas `getRawReplyValue` (pdfHelpers.js) explicitamente NÃO desembrulha
 * valores em array (mesmo padrão de opção `{ id, name }`/`[{ id, name }]` usado em outras
 * perguntas do checklist — ver `normalizeValueToText` no mesmo helper, que já trata esse
 * shape para exibição). Sem este bloco, uma resposta entregue como array de opção única
 * (`[{ id, name: 'Moderado' }]`) caía no ramo de objeto genérico (`typeof === 'object'`),
 * tentava ler `.name`/`.descricao`/`.value` do ARRAY em si (sempre `undefined`) e
 * retornava `null` silenciosamente — perdendo a classificação sem erro visível.
 * Confirmado com teste sintético antes da correção (`scratchpad/test_risk_engine.mjs`,
 * caso "objeto de opção dentro de array"): `resolveClassificacaoId([{id:3,name:'Moderado'}], ...)`
 * retornava `null` em vez de `3`. Corrigido usando o primeiro item do array (resposta
 * única esperada) e reaplicando a mesma resolução recursivamente.
 */
export const resolveClassificacaoId = (valorBruto, vetorReferencia) => {
  if (valorBruto === null || valorBruto === undefined || valorBruto === '') {
    return null
  }

  if (Array.isArray(valorBruto)) {
    return valorBruto.length ? resolveClassificacaoId(valorBruto[0], vetorReferencia) : null
  }

  // Resposta de opção do sistema: objeto com name/descricao (ex.: { id, name: 'Moderado' }).
  const textoCandidato =
    typeof valorBruto === 'object' ? valorBruto.name || valorBruto.descricao || valorBruto.value : valorBruto

  // "Severo ou Crítico" é sinônimo textual de "Muito Alto" para o ID 5 (Probabilidade).
  const sinonimos = { 'muito alto': 'severo ou critico' }

  if (typeof textoCandidato === 'number' || (typeof textoCandidato === 'string' && /^\d+$/.test(textoCandidato))) {
    const idCandidato = Number(textoCandidato)
    if (vetorReferencia.some(item => item.ID === idCandidato)) {
      return idCandidato
    }
    return null
  }

  if (typeof textoCandidato === 'string') {
    const normalizado = normalizeText(textoCandidato)
    const normalizadoFinal = sinonimos[normalizado] || normalizado
    const encontrado = vetorReferencia.find(item => normalizeText(item.descricao) === normalizadoFinal)
    return encontrado ? encontrado.ID : null
  }

  return null
}

/**
 * Consulta a Matriz de Risco pelo par (probabilidadeID, severidadeID) e retorna a
 * classificação de Nível de Risco. Retorna null quando algum dos IDs não foi resolvido,
 * para que o chamador decida o fallback de exibição (nunca "undefined"/"null" no PDF).
 */
export const getNivelRisco = (probabilidadeID, severidadeID) => {
  if (!probabilidadeID || !severidadeID) {
    return null
  }
  const linha = MATRIZ_RISCO.find(item => item.probabilidadeID === probabilidadeID)
  const celula = linha?.severidades.find(item => item.severidadeID === severidadeID)
  return celula ? celula.classificacao : null
}

/**
 * Dado um vetor de respostas de Probabilidade e um vetor de respostas de Severidade
 * (mesmo tamanho, mesma ordem), retorna o vetor de Níveis de Risco na mesma ordem.
 * Cada resposta pode ser número, texto, ID ou objeto de resposta — ver resolveClassificacaoId.
 */
export const calcularNiveisDeRisco = (probabilidadesRecebidas = [], severidadesRecebidas = []) => {
  const tamanho = Math.min(probabilidadesRecebidas.length, severidadesRecebidas.length)
  const niveis = []
  for (let i = 0; i < tamanho; i++) {
    const probabilidadeID = resolveClassificacaoId(probabilidadesRecebidas[i], PROBABILIDADE_REFERENCIA)
    const severidadeID = resolveClassificacaoId(severidadesRecebidas[i], SEVERIDADE_REFERENCIA)
    niveis.push({
      probabilidadeID,
      severidadeID,
      classificacao: getNivelRisco(probabilidadeID, severidadeID)
    })
  }
  return niveis
}

/**
 * Regras "Tipo 1" (Conforto: Ruído, Temperatura, Iluminância). Um valor numérico é
 * comparado a uma faixa/regra que resulta em Probabilidade + Severidade fixas.
 * Fora da faixa "Muito Baixo", o próprio checklist real mostra uma pergunta condicional
 * de Probabilidade/Severidade (ver `cargoEngine.js#buildLinhaConforto`) — aqui a
 * classificação retorna null nesse caso, para o chamador então buscar essa resposta
 * manual em vez de usar um fallback textual.
 *
 * CORREÇÃO (2026-09-09, confirmado via consulta direta ao banco de dados do workspace
 * "bradesco" — tabela `checkList`, coluna `display_conditions` das perguntas de
 * Probabilidade/Severidade filhas de "Ruído - dB (a)"): o limiar real é
 * `{"field": "value", "value": 65, "operator": "gt"}` — a pergunta manual só aparece
 * quando o valor é MAIOR que 65 (`> 65`), ou seja, o valor 65 em si já é
 * "Muito Baixo"/"Irrelevante" automático. A regra anterior usava `< 65` (exclusivo),
 * o que classificava incorretamente o valor exato 65 como "fora da faixa" (retornando
 * null) quando o checklist real não mostra pergunta manual nesse caso. Confirmado com
 * uma resposta real de teste (Ruído = 65, atividade de teste do workspace, 2026-09-02)
 * que não gerou a pergunta manual correspondente.
 */
export const REGRA_RUIDO = valor => {
  const numero = Number(valor)
  if (Number.isNaN(numero)) return null
  return numero <= 65 ? { probabilidade: 'Muito Baixo', severidade: 'Irrelevante' } : null
}

export const REGRA_TEMPERATURA = valor => {
  const numero = Number(valor)
  if (Number.isNaN(numero)) return null
  return numero >= 18 && numero <= 25 ? { probabilidade: 'Muito Baixo', severidade: 'Irrelevante' } : null
}

// Regra confirmada por Julia Buzzo (PM, 2026-08-28): "Se igual ou superior a 500 =
// Muito Baixo". Substitui a faixa "18-25°C" anterior, que era a regra de Temperatura
// duplicada por engano para Iluminância.
export const REGRA_ILUMINANCIA = valor => {
  const numero = Number(valor)
  if (Number.isNaN(numero)) return null
  return numero >= 500 ? { probabilidade: 'Muito Baixo', severidade: 'Irrelevante' } : null
}
